import React from 'react';
import LytSecondaryButton from './LytSecondaryButton';
import PropTypes from 'prop-types';
import LytIcon from '../icon/LytIcon';
import { useTheme } from 'styled-components';
import LytTertiaryButton from './LytTertiaryButton';
import Spinner from '../spinner/Spinner';
import LytPrimaryButton from './LytPrimaryButton';
import LytInvisibleButton from './LytInvisibleButton';
import LytToastButton from './LytToastButton';
import LytDangerButton from './LytDangerButton';
import LytToastCloseButton from './LytToastCloseButton';
import { BUTTON_STYLES, BUTTON_SIZES } from './button-constants';
export { BUTTON_STYLES, BUTTON_SIZES };
const TYPE_MAP = {
    secondary: LytSecondaryButton,
    tertiary: LytTertiaryButton,
    primary: LytPrimaryButton,
    invisible: LytInvisibleButton,
    toast: LytToastButton,
    toast_close: LytToastCloseButton,
    danger: LytDangerButton,
};
const SIZE_MAP = {
    small: 12,
    medium: 16,
    large: 24,
    custom: true,
};
const CLASS_MAP = {
    small: 'lyt-button-small',
    large: 'lyt-button-large',
};
const BUTTON_TYPES = {
    BUTTON: 'button',
    SUBMIT: 'submit',
    RESET: 'reset',
};

/**
 * @description A button component that can be used to create a button with an icon, text, or both.
 * @param {Object} props - The props for the button component.
 * @param {string} props.leftIcon - The name of the icon to display on the left side of the button.
 * @param {string} props.rightIcon - The name of the icon to display on the right side of the button.
 * @param {React.ReactNode} props.rightElement - A custom element to display on the right side of the button (takes precedence over rightIcon).
 * @param {string} props.icon - The name of the icon to display on the button.
 * @param {string} props.type - The type of button to display.
 * @param {string} props.buttonType - The HTML button type attribute (button, submit, reset).
 * @param {string} props.children - The text to display on the button.
 * @param {function} props.onClick - The function to call when the button is clicked.
 * @param {boolean} props.selected - Whether the button is selected.
 * @param {boolean} props.isCompact - Whether the button should use compact padding.
 * @param {boolean} props.loading - Whether to show a loading spinner.
 * @param {boolean} props.disabled - Whether the button is disabled.
 * @param {string|object} props.form - The id of the form the button is associated with.
 */
const LytButton = ({
    leftIcon,
    rightIcon,
    rightElement,
    icon,
    type = 'primary',
    buttonType = BUTTON_TYPES.BUTTON,
    children,
    onClick,
    selected,
    size = 'medium',
    customSize,
    isCompact = false,
    loading = false,
    disabled = false,
    form = null,
    customColor = null,
    ...restProps
}) => {
    const StyledButton = TYPE_MAP[type];
    const theme = useTheme();

    // Get icon color - handle all button types from theme
    const getIconColor = () => {
        if (customColor) return customColor;
        const buttonTheme = theme?.components?.lyt_button?.[type];
        if (!buttonTheme?.text) return theme?.text?.default || '#000';
        return selected ? buttonTheme.text.selected : buttonTheme.text.default;
    };
    const iconColor = getIconColor();
    const leftIconProps = leftIcon
        ? {
              name: leftIcon,
              color: iconColor,
          }
        : null;
    const rightIconProps =
        !rightElement && rightIcon
            ? {
                  name: rightIcon,
                  color: iconColor,
              }
            : null;
    const iconProps = icon
        ? {
              name: icon,
              color: iconColor,
          }
        : null;
    let className = selected ? 'lyt-button-selected' : '';
    if (CLASS_MAP[size]) {
        className += ` ${CLASS_MAP[size]}`;
    }

    // Add loading class if needed
    if (loading) {
        className += ' lyt-button-loading';
    }

    // Determine if this is an icon-only button (has icon but no text content)
    const isIconOnly = !!icon && !children;
    return (
        <StyledButton
            onClick={onClick}
            className={className}
            isIconOnly={isIconOnly}
            isCompact={isCompact}
            disabled={disabled || loading}
            type={buttonType}
            form={form}
            {...(customColor
                ? {
                      customColor,
                  }
                : {})}
            {...restProps}>
            {/* Show spinner when loading */}
            {loading ? (
                <Spinner size={size === 'small' ? 'small' : 'medium'} />
            ) : (
                <>
                    {leftIconProps && <LytIcon {...leftIconProps} />}
                    {iconProps && <LytIcon {...iconProps} />}
                    {children && (
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}>
                            {children}
                        </span>
                    )}
                    {rightElement ||
                        (rightIconProps && <LytIcon {...rightIconProps} />)}
                </>
            )}
        </StyledButton>
    );
};
LytButton.propTypes = {
    leftIcon: PropTypes.string,
    rightIcon: PropTypes.string,
    rightElement: PropTypes.node,
    icon: PropTypes.string,
    type: PropTypes.oneOf(Object.keys(TYPE_MAP)),
    buttonType: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
    children: PropTypes.node,
    onClick: PropTypes.func,
    selected: PropTypes.bool,
    size: PropTypes.oneOf(Object.keys(SIZE_MAP)),
    customSize: PropTypes.number,
    isCompact: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    form: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    customColor: PropTypes.string,
    // Note: isIconOnly is computed internally and not passed as a prop
};
export default LytButton;
